import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { 
  Upload, 
  Download, 
  FileSpreadsheet, 
  CheckCircle, 
  AlertTriangle,
  XCircle,
  Loader2,
  Sparkles,
  Users,
  FileText,
  FileUp,
  User,
  Car,
  Building,
  Calendar,
  Euro,
  Edit,
  RefreshCw,
  Receipt,
  Settings,
  Save,
  Mail,
  History,
  Trash2,
  Eye,
  Undo2,
  Clock,
  ExternalLink,
  Search,
  X,
  UserCheck
} from "lucide-react";
import { toast } from "sonner";

// Import reusable components
import { ClientDataCard, PolicyDataCard, CommissionCalculator, ConfidenceBar, DEFAULT_IMPORT_SETTINGS } from "../components/import";
// 29/09/2026 — elenco canonico dei rami veicolo (include "cvt").
import { isVehiclePolicyType } from "../components/policies";
import { useConferma } from "../components/ConfirmProvider";
// 29/09/2026 — date gg/mm/aaaa da un'unica fonte (storico import: prima
// toLocaleDateString con opzioni hour/minute che NON hanno effetto).
import { formatDate, formatDateTime } from "../lib/format";

// 30/09/2026 — avvisi dell'import quando un numero di polizza esiste già.
// Un solo posto per il testo: il backend manda già `messaggio` (unica fonte),
// qui c'è solo il testo di riserva se la risposta fosse di una versione vecchia.
function avvisiDaRisposta(data) {
  const avvisi = [];
  if (data && data.policy_number_exists) {
    const e = data.policy_number_exists;
    const numero = e.policy_number || data.policy_number || "";
    const dettagli = [];
    if (e.company) dettagli.push(e.company);
    if (e.license_plate) dettagli.push(`targa ${String(e.license_plate).toUpperCase()}`);
    if (e.start_date || e.expiry_date) {
      dettagli.push(`dal ${formatDate(e.start_date)} al ${formatDate(e.expiry_date)}`);
    }
    const testo = e.messaggio
      || `Numero polizza ${numero}: esiste già in archivio ed è la polizza di ${e.client_name || "cliente non trovato"}`
         + (dettagli.length ? ` (${dettagli.join(", ")})` : "")
         + `. Non è stata creata una seconda polizza: apri la polizza esistente ${numero} per controllare se è davvero la stessa.`;
    avvisi.push({ tipo: "numero_presente", numero, policy_id: e.policy_id, testo });
  }
  (data && data.documents_skipped ? data.documents_skipped : []).forEach((d) => {
    avvisi.push({ tipo: "documento_saltato", testo: `Documento non archiviato: ${d.motivo}` });
  });
  return avvisi;
}

export default function ImportPage() {
  const [importType, setImportType] = useState("clients");
  const conferma = useConferma();
  const navigate = useNavigate();
  // 30/09/2026 — avvisi PERSISTENTI dell'import PDF (non solo un toast che
  // sparisce): servono a leggere con calma il numero della polizza esistente
  // e ad aprirla. Prima l'utente vedeva solo un errore 500.
  const [importAvvisi, setImportAvvisi] = useState([]);
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  
  // PDF Import State
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfFileBase64, setPdfFileBase64] = useState(null); // Store base64 immediately after selection
  const [pdfAnalyzing, setPdfAnalyzing] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [pdfCreating, setPdfCreating] = useState(false);
  const [editingPdf, setEditingPdf] = useState(false);
  const [pdfCreateAlias, setPdfCreateAlias] = useState(false); // Email alias for PDF import
  
  // Client Override State (for manually selecting a different client)
  const [allClients, setAllClients] = useState([]);
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [clientOverride, setClientOverride] = useState(null); // null = use AI result
  
  // Drag and Drop State
  const [isDraggingPdf, setIsDraggingPdf] = useState(false);
  const [isDraggingExcel, setIsDraggingExcel] = useState(false);
  
  // Import Settings State
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [importSettings, setImportSettings] = useState(() => {
    // Load from localStorage or use defaults
    const saved = localStorage.getItem("importSettings");
    return saved ? { ...DEFAULT_IMPORT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_IMPORT_SETTINGS;
  });

  // Email Import State
  const [emailSettings, setEmailSettings] = useState({
    enabled: false,
    email_address: "",
    app_password: "",
    check_interval_minutes: 5,
    auto_delete_after_days: 180,
    auto_process: false,
    has_password: false
  });
  const [emailSettingsLoading, setEmailSettingsLoading] = useState(true);
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailTesting, setEmailTesting] = useState(false);
  const [emailFetching, setEmailFetching] = useState(false);
  const [pendingImports, setPendingImports] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [selectedImport, setSelectedImport] = useState(null);
  const [importAnalyzing, setImportAnalyzing] = useState(false);
  const [importConfirming, setImportConfirming] = useState(false);
  
  // Email Import Data - stesso formato di pdfData per editing completo
  const [emailImportData, setEmailImportData] = useState(null);
  const [editingEmailImport, setEditingEmailImport] = useState(true); // Default editabile
  const [emailCreateAlias, setEmailCreateAlias] = useState(false); // Email alias for Email import

  // Import History State
  const [importHistory, setImportHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Services Catalog State
  const [servicesCatalog, setServicesCatalog] = useState([]);

  // Load email settings on mount + fetch clients list + fetch services
  useEffect(() => {
    loadEmailSettings();
    fetchAllClients();
    fetchServicesCatalog();
  }, []);

  const fetchServicesCatalog = async () => {
    try {
      const response = await axios.get("/api/services");
      setServicesCatalog(Array.isArray(response.data) ? response.data : []);
    } catch {
      // services catalog may be empty
    }
  };

  const fetchAllClients = async () => {
    try {
      const response = await axios.get("/api/clients?limit=500");
      // Handle both paginated {items: [...]} and direct array formats
      setAllClients(response.data.items || response.data.clients || (Array.isArray(response.data) ? response.data : []));
    } catch {
      // clients will be empty
    }
  };

  const loadEmailSettings = async () => {
    try {
      const response = await axios.get("/api/email-import/settings");
      setEmailSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      // Email settings may not exist yet
    } finally {
      setEmailSettingsLoading(false);
    }
  };

  const saveEmailSettings = async () => {
    setEmailSaving(true);
    try {
      await axios.post("/api/email-import/settings", emailSettings);
      toast.success("Impostazioni email salvate");
      await loadEmailSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setEmailSaving(false);
    }
  };

  const testEmailConnection = async () => {
    setEmailTesting(true);
    try {
      const response = await axios.post("/api/email-import/test-connection");
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella connessione");
    } finally {
      setEmailTesting(false);
    }
  };

  const fetchEmailsNow = async () => {
    setEmailFetching(true);
    try {
      const response = await axios.post("/api/email-import/fetch-now");
      toast.success(response.data.message);
      loadPendingImports();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel recupero email");
    } finally {
      setEmailFetching(false);
    }
  };

  const loadPendingImports = async () => {
    setPendingLoading(true);
    try {
      const response = await axios.get("/api/email-import/pending");
      setPendingImports(response.data);
    } catch (error) {
      toast.error("Errore caricamento import in attesa");
    } finally {
      setPendingLoading(false);
    }
  };

  const analyzeEmailImport = async (importItem) => {
    setSelectedImport(importItem);
    setImportAnalyzing(true);
    try {
      // Analizza con AI
      await axios.post(`/api/email-import/pending/${importItem.import_id}/analyze`);
      // Ricarica i dettagli con l'analisi
      const detailResponse = await axios.get(`/api/email-import/pending/${importItem.import_id}`);
      const data = detailResponse.data;
      
      // Converti nel formato pdfData per editing completo
      setEmailImportData({
        client_data: data.ai_analysis?.client_data || {},
        policy_data: data.ai_analysis?.policy_data || {},
        client_exists: data.ai_analysis?.client_exists || false,
        existing_client: data.ai_analysis?.existing_client || null,
        confidence: data.ai_analysis?.confidence || 0,
        total_collected: "",
        // Info sull'allegato per upload documento
        attachment_info: {
          filename: data.attachment_name,
          content_base64: data.attachment_data,
          attachment_type: data.attachment_type,
          email_from: data.email_from,
          email_subject: data.email_subject
        }
      });
      setSelectedImport(data);
      toast.success("Analisi AI completata - Modifica i dati e conferma");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'analisi");
      setSelectedImport(null);
    } finally {
      setImportAnalyzing(false);
    }
  };

  const updateEmailClientData = (field, value) => {
    setEmailImportData(prev => ({
      ...prev,
      client_data: { ...prev.client_data, [field]: value }
    }));
  };

  const updateEmailPolicyData = (field, value) => {
    setEmailImportData(prev => ({
      ...prev,
      policy_data: { ...prev.policy_data, [field]: value }
    }));
  };

  const handleEmailImportCreate = async () => {
    if (!emailImportData || !selectedImport) return;
    
    setImportConfirming(true);
    setImportAvvisi([]);
    try {
      // Usa lo stesso endpoint create-from-pdf per consistenza
      const response = await axios.post("/api/ai/create-from-pdf", {
        client_data: emailImportData.client_data,
        policy_data: emailImportData.policy_data,
        use_existing_client: emailImportData.client_exists,
        existing_client_id: emailImportData.existing_client?.client_id,
        pdf_info: {
          filename: emailImportData.attachment_info?.filename,
          content_base64: emailImportData.attachment_info?.content_base64
        },
        detected_documents: [],
        import_source: "email", // Per tracking nello storico
        create_alias: emailCreateAlias && !emailImportData.client_exists // Only if new client
      });

      let message = "";
      if (response.data.client_created) {
        message += "Cliente creato. ";
        // Show alias creation result
        if (response.data.alias_created) {
          message += `Alias: ${response.data.email_alias}. `;
        } else if (response.data.alias_error) {
          toast.warning(`Alias non creato: ${response.data.alias_error}`);
        }
      } else if (response.data.client_already_existed) {
        message += `Cliente trovato tramite CF: ${response.data.existing_client_name}. `;
      } else if (response.data.client_id) {
        message += "Cliente esistente collegato. ";
      }
      if (response.data.policy_created) {
        message += "Polizza creata. ";
      }
      if (response.data.document_uploaded) {
        message += "Documento archiviato.";
      }

      // 30/09/2026 — stesso avviso anche confermando un import da email:
      // se il numero esiste già non si crea un doppione e lo si dice.
      const nuoviAvvisi = avvisiDaRisposta(response.data);
      nuoviAvvisi.forEach((a) => {
        if (a.tipo === "numero_presente") toast.warning(`⚠️ ${a.testo}`, { duration: 15000 });
      });
      if (typeof response.data.policies_skipped === "number" && response.data.policies_skipped > 0) {
        message += `Creati ${response.data.policies_created || 0}, saltati ${response.data.skipped_count || response.data.policies_skipped}. `;
      }
      if (nuoviAvvisi.length) setImportAvvisi(nuoviAvvisi);

      toast.success(message || "Import completato!");
      
      // Segna come completato nel sistema email
      await axios.post(`/api/email-import/pending/${selectedImport.import_id}/discard`);
      
      // Reset
      setEmailImportData(null);
      setSelectedImport(null);
      setEmailCreateAlias(false); // Reset alias checkbox
      loadPendingImports();
      loadImportHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione");
    } finally {
      setImportConfirming(false);
    }
  };

  const discardEmailImport = async (importId) => {
    try {
      await axios.post(`/api/email-import/pending/${importId}/discard`);
      toast.success("Import scartato");
      setSelectedImport(null);
      setEmailImportData(null);
      loadPendingImports();
    } catch (error) {
      toast.error("Errore");
    }
  };

  const loadImportHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await axios.get("/api/email-import/history");
      setImportHistory(response.data);
    } catch (error) {
      toast.error("Errore caricamento storico");
    } finally {
      setHistoryLoading(false);
    }
  };

  const undoImport = async (historyId) => {
    try {
      await axios.post(`/api/email-import/history/${historyId}/undo`);
      toast.success("Operazione annullata");
      loadImportHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'annullamento");
    }
  };

  // Save settings to localStorage when changed
  const saveSettings = () => {
    localStorage.setItem("importSettings", JSON.stringify(importSettings));
    toast.success("Impostazioni salvate");
    setSettingsOpen(false);
  };

  // Auto-calculate fields when AI populates data (respecting settings)
  // Also re-calculate when user manually changes fiscal code
  useEffect(() => {
    const processAIData = async () => {
      if (!importSettings.autoCalculateBirthDate) return;
      if (!pdfData?.client_data?.fiscal_code) return;
      // Skip for business clients - P.IVA can't be parsed for birth data
      if (pdfData?.client_data?.client_type === "business") return;
      
      const cf = pdfData.client_data.fiscal_code;
      // Skip if it looks like a P.IVA (11 digits, no letters)
      if (/^\d{11}$/.test(cf)) return;
      // Skip if CF is not complete (16 chars)
      if (cf.length !== 16) return;
      
      // Parse fiscal code to get birth data (always recalculate - CF is source of truth)
      try {
        const response = await axios.get(`/api/clients/parse-fiscal-code/${cf}`);
        if (response.data.valid) {
          setPdfData(prev => ({
            ...prev,
            client_data: {
              ...prev.client_data,
              birth_date: response.data.birth_date || prev.client_data?.birth_date || "",
              birth_place: response.data.birth_place || prev.client_data?.birth_place || "",
              birth_place_province: response.data.estero ? "EE" : (response.data.birth_place_province || ""),
              gender: response.data.gender || prev.client_data?.gender || ""
            }
          }));
        }
      } catch (error) {
        // Silently fail - manual entry still possible
      }
    };
    
    processAIData();
  }, [pdfData?.client_data?.fiscal_code, importSettings.autoCalculateBirthDate]);

  // Auto-calculate semiannual expiry when expiry_date or payment_frequency changes
  useEffect(() => {
    if (!importSettings.autoCalculateSemiannual) return;
    if (!pdfData?.policy_data) return;
    
    const { expiry_date, payment_frequency } = pdfData.policy_data;
    
    if (payment_frequency === "semiannual" && expiry_date && !pdfData.policy_data.semiannual_expiry) {
      // Calculate semiannual date (6 months before annual expiry)
      try {
        const expiryParts = expiry_date.split("-");
        if (expiryParts.length === 3) {
          const expiryDateObj = new Date(expiryParts[0], expiryParts[1] - 1, expiryParts[2]);
          expiryDateObj.setMonth(expiryDateObj.getMonth() - 6);
          const semiannualDate = expiryDateObj.toISOString().split("T")[0];
          
          setPdfData(prev => ({
            ...prev,
            policy_data: {
              ...prev.policy_data,
              semiannual_expiry: semiannualDate
            }
          }));
          // Debug removed
        }
      } catch (error) {
        // Debug removed
      }
    }
  }, [pdfData?.policy_data?.expiry_date, pdfData?.policy_data?.payment_frequency]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview(null);
    }
  };

  // Service helpers for PDF import
  const addServiceToPdf = (service) => {
    setPdfData(prev => ({
      ...prev,
      policy_data: {
        ...prev.policy_data,
        additional_services: [
          ...(prev.policy_data?.additional_services || []),
          service
        ]
      }
    }));
  };

  const removeServiceFromPdf = (idx) => {
    setPdfData(prev => ({
      ...prev,
      policy_data: {
        ...prev.policy_data,
        additional_services: (prev.policy_data?.additional_services || []).filter((_, i) => i !== idx)
      }
    }));
  };

  // Service helpers for Email import
  const addServiceToEmailImport = (service) => {
    setEmailImportData(prev => ({
      ...prev,
      policy_data: {
        ...prev.policy_data,
        additional_services: [
          ...(prev.policy_data?.additional_services || []),
          service
        ]
      }
    }));
  };

  const removeServiceFromEmailImport = (idx) => {
    setEmailImportData(prev => ({
      ...prev,
      policy_data: {
        ...prev.policy_data,
        additional_services: (prev.policy_data?.additional_services || []).filter((_, i) => i !== idx)
      }
    }));
  };

  const handleAnalyze = async () => {
    if (!file) {
      toast.error("Seleziona un file Excel");
      return;
    }

    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `/api/import/analyze-excel?import_type=${importType}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setPreview(response.data);
      toast.success(`Analisi completata: ${response.data.valid_rows} righe valide`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'analisi del file");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleImport = async () => {
    if (!preview || !preview.all_data) {
      toast.error("Nessun dato da importare");
      return;
    }

    // v3.9.12 — import in blocco non annullabile: conferma esplicita con il conteggio
    const totale = Array.isArray(preview.all_data) ? preview.all_data.length : 0;
    if (!(await conferma({
      titolo: `Confermi l'import di ${totale} righe?`,
      descrizione:
        "L'import crea i record in blocco e NON è annullabile dall'app. " +
      "Se lo esegui due volte sullo stesso file, i dati vengono duplicati.",
    }))) return;

    setImporting(true);
    try {
      const response = await axios.post("/api/import/execute", {
        import_type: importType,
        data: preview.all_data
      });

      toast.success(response.data.message);
      setPreview(null);
      setFile(null);
      
      // Reset file input
      const fileInput = document.getElementById("excel-file-input");
      if (fileInput) fileInput.value = "";
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante l'import");
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(
        `/api/import/template?template_type=${importType}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `template_${importType}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Template scaricato");
    } catch (error) {
      toast.error("Errore nel download del template");
    }
  };

  const getFieldsForType = () => {
    if (importType === "clients") {
      return ["name", "email", "phone", "fiscal_code", "address"];
    }
    return ["client_name", "policy_number", "company", "policy_type", "premium", "expiry_date"];
  };

  // PDF Import Functions
  const handlePdfFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      await processPdfFile(selectedFile);
    }
  };

  // Process PDF file (shared between input and drag&drop)
  const processPdfFile = async (selectedFile) => {
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    if (!allowedTypes.includes(selectedFile.type) && !selectedFile.name.match(/\.(pdf|jpg|jpeg|png|webp|heic|heif)$/i)) {
      toast.error("Formato file non supportato. Usa PDF o immagini (JPG, PNG, WebP)");
      return;
    }

    // Validate file size (max 20MB)
    const MAX_SIZE_MB = 20;
    if (selectedFile.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`File troppo grande (${(selectedFile.size / 1024 / 1024).toFixed(1)} MB). Limite: ${MAX_SIZE_MB} MB`);
      return;
    }

    setPdfFile(selectedFile);
    setPdfData(null);
    setEditingPdf(false);
    
    // Convert to base64 IMMEDIATELY and store it
    try {
      const base64Content = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result;
          const base64 = result.includes(',') ? result.split(',')[1] : result;
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(selectedFile);
      });
      
      setPdfFileBase64({
        filename: selectedFile.name,
        content_base64: base64Content,
        mime_type: selectedFile.type || "application/pdf"
      });
    } catch (error) {
      toast.error("Errore lettura file");
      setPdfFileBase64(null);
    }
  };

  // Drag and Drop handlers for PDF
  const handlePdfDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handlePdfDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPdf(true);
  };

  const handlePdfDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if leaving the drop zone (not entering a child)
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDraggingPdf(false);
  };

  const handlePdfDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPdf(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await processPdfFile(files[0]);
    }
  };

  // Drag and Drop handlers for Excel
  const handleExcelDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleExcelDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingExcel(true);
  };

  const handleExcelDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDraggingExcel(false);
  };

  const handleExcelDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingExcel(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const droppedFile = files[0];
      if (droppedFile.name.match(/\.(xlsx|xls)$/i)) {
        setFile(droppedFile);
      } else {
        toast.error("Formato file non supportato. Usa file Excel (.xlsx, .xls)");
      }
    }
  };

  const handlePdfAnalyze = async () => {
    if (!pdfFile) {
      toast.error("Seleziona un file PDF");
      return;
    }

    setPdfAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append("file", pdfFile);

      const response = await axios.post("/api/ai/parse-pdf-complete", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setPdfData(response.data);
      setEditingPdf(true); // Show all fields in edit mode by default
      
      // Show warnings from AI validation
      const warnings = response.data.warnings || [];
      if (warnings.length > 0) {
        warnings.forEach(w => {
          toast.warning(w.message, { duration: 8000 });
        });
      }
      
      // Show different message based on detected documents
      if (response.data.has_multiple_documents) {
        const docTypes = Object.keys(response.data.documents_by_type || {});
        toast.success(`PDF analizzato: ${response.data.pages_processed} pagine, ${docTypes.length} tipi documento rilevati`);
      } else {
        toast.success(`PDF analizzato: ${response.data.pages_processed} pagine elaborate`);
      }
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || "";
      if (status === 413) {
        toast.error(detail || "File troppo grande. Riduci la dimensione e riprova.");
      } else if (status === 400) {
        toast.error(detail || "File non valido o corrotto.");
      } else if (status === 422) {
        toast.error(detail || "Nessun dato estraibile dal documento.");
      } else if (status === 429) {
        toast.error(detail || "Troppe richieste. Riprova tra qualche minuto.");
      } else if (status === 504) {
        toast.error(detail || "Timeout nell'analisi. Riprova.");
      } else {
        toast.error(detail || "Errore nell'analisi del PDF");
      }
    } finally {
      setPdfAnalyzing(false);
    }
  };

  const handlePdfCreate = async () => {
    if (!pdfData) return;

    setPdfCreating(true);
    setImportAvvisi([]);
    try {
      // Use the pre-saved base64 content (more reliable on mobile)
      const pdfInfoData = pdfFileBase64 || {};
      
      // If user manually selected a different client, use that
      const useExisting = clientOverride ? true : pdfData.client_exists;
      const existingId = clientOverride ? clientOverride.client_id : pdfData.existing_client?.client_id;

      const response = await axios.post("/api/ai/create-from-pdf", {
        client_data: clientOverride ? null : pdfData.client_data,
        policy_data: pdfData.policy_data,
        use_existing_client: useExisting,
        existing_client_id: existingId,
        pdf_info: pdfInfoData,
        detected_documents: pdfData.detected_documents || [],
        create_alias: !useExisting && pdfCreateAlias
      });

      let message = "";
      if (response.data.client_created) {
        message += "Cliente creato. ";
        // Show alias creation result
        if (response.data.alias_created) {
          message += `Alias: ${response.data.email_alias}. `;
        } else if (response.data.alias_error) {
          toast.warning(`Alias non creato: ${response.data.alias_error}`);
        }
      } else if (response.data.client_already_existed) {
        message += `Cliente trovato tramite CF: ${response.data.existing_client_name}. `;
      } else if (response.data.client_id) {
        message += "Cliente esistente collegato. ";
      }
      if (response.data.policy_created) {
        message += "Polizza creata. ";
      }
      
      // Handle multiple documents saved (split PDF)
      if (response.data.saved_documents && response.data.saved_documents.length > 0) {
        const docCount = response.data.saved_documents.length;
        const docTypes = [...new Set(response.data.saved_documents.map(d => d.type))];
        const typeLabels = {
          "polizza": "Polizza",
          "carta_identita": "Carta Identità",
          "patente": "Patente",
          "tessera_sanitaria": "Tessera Sanitaria",
          "codice_fiscale": "Codice Fiscale",
          "permesso_soggiorno": "Permesso Soggiorno",
          "libretto_circolazione": "Libretto",
          "altro": "Documento"
        };
        const typeNames = docTypes.map(t => typeLabels[t] || t).join(", ");
        message += `📄 ${docCount} documenti separati e salvati (${typeNames})`;
      } else if (response.data.document_created) {
        const savedTo = response.data.document_saved_to === "client" ? "archivio Cliente" : "archivio Polizza";
        const docType = response.data.document_type || "documento";
        const storage = response.data.document_storage || "";
        message += `📄 ${docType} salvato in ${savedTo}`;
        if (storage) {
          message += ` (${storage})`;
        }
      }
      
      // Show auto-renewal message if applicable
      if (response.data.auto_renewed_old_policy) {
        const oldPolicyNum = response.data.auto_renewed_policy_number || "";
        toast.info(`🔄 Polizza precedente ${oldPolicyNum} segnata come rinnovata e spostata nello storico`, { duration: 6000 });
      }

      // 29/09/2026 — avviso INFORMATIVO (non bloccante): esiste una polizza
      // dello STESSO tipo che si sovrappone nel tempo. Auto e CVT non si
      // segnalano piu' fra loro (services/regole_polizza.e_sovrapposizione).
      if (response.data.overlapping_policies && response.data.overlapping_policies.length > 0) {
        const o = response.data.overlapping_policies[0];
        toast.warning(
          `⚠️ Avviso (la polizza è stata creata comunque): questa polizza ${o.policy_type || ""} si sovrappone a ${o.policy_number} (${o.company || "altra compagnia"}, ${formatDate(o.start_date)} → ${formatDate(o.expiry_date)}), dello STESSO tipo. Controlla se è una sostituzione. La polizza precedente non l'ho toccata.`,
          { duration: 12000 }
        );
      }

      // 30/09/2026 — numero polizza GIA' IN ARCHIVIO (non solo per questo
      // cliente): messaggio esplicito con numero completo, cliente, compagnia,
      // targa e periodo, avviso persistente in pagina e possibilità di APRIRE
      // la polizza esistente. Il resto dell'import prosegue.
      const nuoviAvvisi = avvisiDaRisposta(response.data);
      nuoviAvvisi.forEach((a) => {
        if (a.tipo === "numero_presente") toast.warning(`⚠️ ${a.testo}`, { duration: 15000 });
      });

      // 30/09/2026 — riepilogo creati / saltati
      if (typeof response.data.policies_skipped === "number" && response.data.policies_skipped > 0) {
        const creati = response.data.policies_created || 0;
        const saltati = response.data.skipped_count || response.data.policies_skipped;
        message += `Creati ${creati}, saltati ${saltati}. `;
      }
      if (nuoviAvvisi.length) setImportAvvisi(nuoviAvvisi);

      toast.success(message || "Operazione completata");
      setPdfData(null);
      setPdfFile(null);
      setPdfFileBase64(null);
      setEditingPdf(false);
      setPdfCreateAlias(false);
      setClientOverride(null);
      setShowClientSearch(false);
      setClientSearchQuery("");
      
      // Reset file input
      const fileInput = document.getElementById("pdf-file-input");
      if (fileInput) fileInput.value = "";
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione");
    } finally {
      setPdfCreating(false);
    }
  };

  const updatePdfClientData = (field, value) => {
    setPdfData(prev => ({
      ...prev,
      client_data: { ...prev.client_data, [field]: value }
    }));
  };

  const updatePdfPolicyData = (field, value) => {
    setPdfData(prev => ({
      ...prev,
      policy_data: { ...prev.policy_data, [field]: value }
    }));
  };

  return (
    <div data-testid="import-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground tracking-tight flex items-center gap-3">
            <Upload className="h-8 w-8 text-primary" />
            Import Dati
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Importa clienti e polizze da Excel, PDF o foto
          </p>
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={() => setSettingsOpen(true)}
          title="Impostazioni Import"
        >
          <Settings className="h-5 w-5" />
        </Button>
      </div>

      {/* 30/09/2026 — AVVISI dell'ultimo import (persistenti, non toast che
          sparisce): il numero della polizza esistente si legge con calma e si
          puo' aprire la scheda della polizza. */}
      {importAvvisi.length > 0 && (
        <Card
          className="border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950"
          data-testid="import-avvisi"
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Attenzione: controlla prima di rifare l'import
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {importAvvisi.map((a, idx) => (
              <div key={idx} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-amber-900 dark:text-amber-100">
                  <AlertTriangle className="h-4 w-4 inline mr-1 -mt-0.5" />
                  {a.testo}
                </p>
                {a.tipo === "numero_presente" && a.policy_id && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="whitespace-nowrap"
                    onClick={() => navigate(`/policies/${a.policy_id}`)}
                    data-testid="apri-polizza-esistente"
                  >
                    Apri la polizza esistente {a.numero ? `(${a.numero})` : ""}
                  </Button>
                )}
              </div>
            ))}
            <div className="flex justify-end">
              <Button size="sm" variant="ghost" onClick={() => setImportAvvisi([])}>
                Ho capito, chiudi l'avviso
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Impostazioni Import Dati
            </DialogTitle>
            <DialogDescription>
              Personalizza il comportamento dell'import e dell'analisi AI
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Sezione Gestione Documenti */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                📁 Gestione Documenti
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Rinomina automatica file</Label>
                    <p className="text-xs text-muted-foreground">L'AI rinomina i documenti in modo intelligente</p>
                  </div>
                  <Switch
                    checked={importSettings.autoRename}
                    onCheckedChange={(checked) => setImportSettings(prev => ({ ...prev, autoRename: checked }))}
                  />
                </div>
                
                {importSettings.autoRename && (
                  <div className="ml-4 pl-4 border-l-2">
                    <Label className="text-xs">Formato nome file</Label>
                    <Select
                      value={importSettings.fileNameFormat}
                      onValueChange={(value) => setImportSettings(prev => ({ ...prev, fileNameFormat: value }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="{tipo}_{cliente}_{data}">Tipo_Cliente_Data</SelectItem>
                        <SelectItem value="{cliente}_{tipo}_{data}">Cliente_Tipo_Data</SelectItem>
                        <SelectItem value="{tipo}_{numero_polizza}">Tipo_NumeroPolizza</SelectItem>
                        <SelectItem value="{compagnia}_{tipo}_{cliente}">Compagnia_Tipo_Cliente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Separa documenti multipli</Label>
                    <p className="text-xs text-muted-foreground">Divide PDF con più documenti (es: polizza + carta identità)</p>
                  </div>
                  <Switch
                    checked={importSettings.splitMultipleDocuments}
                    onCheckedChange={(checked) => setImportSettings(prev => ({ ...prev, splitMultipleDocuments: checked }))}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Salva in locale</Label>
                    <p className="text-xs text-muted-foreground">Salva i documenti nello storage locale</p>
                  </div>
                  <Switch
                    checked={importSettings.saveToLocal}
                    onCheckedChange={(checked) => setImportSettings(prev => ({ ...prev, saveToLocal: checked }))}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Salva su MEGA</Label>
                    <p className="text-xs text-muted-foreground">Carica automaticamente su MEGA (se connesso)</p>
                  </div>
                  <Switch
                    checked={importSettings.saveToDrive}
                    onCheckedChange={(checked) => setImportSettings(prev => ({ ...prev, saveToDrive: checked }))}
                  />
                </div>
              </div>
            </div>
            
            {/* Sezione Comportamento AI */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                🤖 Comportamento AI
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Calcolo automatico data nascita</Label>
                    <p className="text-xs text-muted-foreground">Estrae data e luogo nascita dal codice fiscale</p>
                  </div>
                  <Switch
                    checked={importSettings.autoCalculateBirthDate}
                    onCheckedChange={(checked) => setImportSettings(prev => ({ ...prev, autoCalculateBirthDate: checked }))}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Calcolo automatico scadenza semestrale</Label>
                    <p className="text-xs text-muted-foreground">Calcola la rata semestrale dalla scadenza annuale</p>
                  </div>
                  <Switch
                    checked={importSettings.autoCalculateSemiannual}
                    onCheckedChange={(checked) => setImportSettings(prev => ({ ...prev, autoCalculateSemiannual: checked }))}
                  />
                </div>
                
                <div>
                  <Label>Matching cliente esistente</Label>
                  <p className="text-xs text-muted-foreground mb-2">Cosa fare quando trova un cliente già presente</p>
                  <Select
                    value={importSettings.clientMatching}
                    onValueChange={(value) => setImportSettings(prev => ({ ...prev, clientMatching: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ask">Chiedi sempre conferma</SelectItem>
                      <SelectItem value="auto">Usa automaticamente se trovato</SelectItem>
                      <SelectItem value="always_new">Crea sempre nuovo cliente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Confidenza minima AI</Label>
                  <p className="text-xs text-muted-foreground mb-2">Sotto questa soglia richiede conferma manuale</p>
                  <Select
                    value={importSettings.minConfidence.toString()}
                    onValueChange={(value) => setImportSettings(prev => ({ ...prev, minConfidence: parseInt(value) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="60">60% - Permissivo</SelectItem>
                      <SelectItem value="70">70% - Normale</SelectItem>
                      <SelectItem value="80">80% - Accurato</SelectItem>
                      <SelectItem value="90">90% - Molto accurato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            {/* Sezione Campi Obbligatori */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                📝 Campi Obbligatori
              </h3>
              <p className="text-xs text-muted-foreground">Seleziona i campi richiesti prima di poter salvare</p>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="req-clientName"
                    checked={importSettings.requiredFields.clientName}
                    onCheckedChange={(checked) => setImportSettings(prev => ({
                      ...prev,
                      requiredFields: { ...prev.requiredFields, clientName: checked }
                    }))}
                  />
                  <label htmlFor="req-clientName" className="text-sm cursor-pointer">Nome cliente</label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="req-fiscalCode"
                    checked={importSettings.requiredFields.fiscalCode}
                    onCheckedChange={(checked) => setImportSettings(prev => ({
                      ...prev,
                      requiredFields: { ...prev.requiredFields, fiscalCode: checked }
                    }))}
                  />
                  <label htmlFor="req-fiscalCode" className="text-sm cursor-pointer">Codice Fiscale</label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="req-policyNumber"
                    checked={importSettings.requiredFields.policyNumber}
                    onCheckedChange={(checked) => setImportSettings(prev => ({
                      ...prev,
                      requiredFields: { ...prev.requiredFields, policyNumber: checked }
                    }))}
                  />
                  <label htmlFor="req-policyNumber" className="text-sm cursor-pointer">Numero Polizza</label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="req-company"
                    checked={importSettings.requiredFields.company}
                    onCheckedChange={(checked) => setImportSettings(prev => ({
                      ...prev,
                      requiredFields: { ...prev.requiredFields, company: checked }
                    }))}
                  />
                  <label htmlFor="req-company" className="text-sm cursor-pointer">Compagnia</label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="req-premium"
                    checked={importSettings.requiredFields.premium}
                    onCheckedChange={(checked) => setImportSettings(prev => ({
                      ...prev,
                      requiredFields: { ...prev.requiredFields, premium: checked }
                    }))}
                  />
                  <label htmlFor="req-premium" className="text-sm cursor-pointer">Premio</label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="req-licensePlate"
                    checked={importSettings.requiredFields.licensePlate}
                    onCheckedChange={(checked) => setImportSettings(prev => ({
                      ...prev,
                      requiredFields: { ...prev.requiredFields, licensePlate: checked }
                    }))}
                  />
                  <label htmlFor="req-licensePlate" className="text-sm cursor-pointer">Targa (veicoli)</label>
                </div>
              </div>
            </div>
            
            {/* Sezione Mapping Documenti */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                🏷️ Cartelle Documenti
              </h3>
              <p className="text-xs text-muted-foreground">
                I documenti veicolo (polizze auto/moto e libretti) vengono salvati in sottocartelle con la targa
              </p>
              
              <div className="space-y-2">
                {/* Vehicle documents - fixed to plate folder */}
                <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">📁 Documenti Veicolo (in Polizze/{"{TARGA}"})</p>
                  <div className="flex items-center justify-between pl-2">
                    <span className="text-sm">🚗 Polizza Veicolo</span>
                    <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">
                      Polizze/{"{TARGA}"}/
                    </span>
                  </div>
                  <div className="flex items-center justify-between pl-2">
                    <span className="text-sm">📖 Libretto Circolazione</span>
                    <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">
                      Polizze/{"{TARGA}"}/
                    </span>
                  </div>
                </div>
                
                {/* Other policies */}
                <div className="flex items-center justify-between">
                  <span className="text-sm">📋 Polizza (senza targa)</span>
                  <span className="text-xs bg-muted px-2 py-1 rounded">Polizze/</span>
                </div>
                
                {/* Personal documents - configurable */}
                <div className="border-t pt-2 mt-2">
                  <p className="text-xs font-medium text-muted-foreground mb-2">📁 Documenti Personali</p>
                  {Object.entries({
                    carta_identita: { label: "🪪 Carta Identità" },
                    patente: { label: "🚗 Patente" },
                    tessera_sanitaria: { label: "🏥 Tessera Sanitaria" },
                    codice_fiscale: { label: "📇 Codice Fiscale" },
                    permesso_soggiorno: { label: "📜 Permesso Soggiorno" },
                    visura_camerale: { label: "🏢 Visura Camerale" },
                    altro: { label: "📄 Altro" }
                  }).map(([key, info]) => (
                    <div key={key} className="flex items-center justify-between py-1">
                      <span className="text-sm">{info.label}</span>
                      <Select
                        value={importSettings.documentMapping[key]}
                        onValueChange={(value) => setImportSettings(prev => ({
                          ...prev,
                          documentMapping: { ...prev.documentMapping, [key]: value }
                        }))}
                      >
                        <SelectTrigger className="w-40 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Documenti">📁 Documenti</SelectItem>
                          <SelectItem value="Documenti/Personali">📁 Documenti/Personali</SelectItem>
                          <SelectItem value="Documenti/Azienda">📁 Documenti/Azienda</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Footer */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>
              Annulla
            </Button>
            <Button onClick={saveSettings}>
              <Save className="h-4 w-4 mr-2" />
              Salva Impostazioni
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="pdf" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 max-w-2xl">
          <TabsTrigger value="pdf" data-testid="tab-pdf">
            <FileUp className="h-4 w-4 mr-2" />
            PDF / Foto
          </TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">
            <Mail className="h-4 w-4 mr-2" />
            Da Email
          </TabsTrigger>
          <TabsTrigger value="excel" data-testid="tab-excel">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Excel
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <History className="h-4 w-4 mr-2" />
            Storico
          </TabsTrigger>
        </TabsList>

        {/* PDF TAB */}
        <TabsContent value="pdf" className="space-y-4">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Analisi Documento con AI
              </CardTitle>
              <CardDescription>
                Carica un PDF o scatta una foto della polizza - l'AI estrarrà automaticamente tutti i dati
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* PDF/Image Upload with Drag & Drop */}
              <div 
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
                  isDraggingPdf 
                    ? 'border-primary bg-primary/10 scale-[1.02]' 
                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
                }`}
                onDragOver={handlePdfDragOver}
                onDragEnter={handlePdfDragEnter}
                onDragLeave={handlePdfDragLeave}
                onDrop={handlePdfDrop}
                data-testid="pdf-dropzone"
              >
                <FileUp className={`h-12 w-12 mx-auto mb-4 transition-colors ${isDraggingPdf ? 'text-primary' : 'text-muted-foreground'}`} />
                
                <input
                  id="pdf-file-input"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
                  capture="environment"
                  onChange={handlePdfFileChange}
                  className="hidden"
                />
                
                {isDraggingPdf ? (
                  <div className="text-lg font-medium text-primary">
                    Rilascia il file qui!
                  </div>
                ) : (
                  <label htmlFor="pdf-file-input" className="cursor-pointer">
                    <div className="text-lg font-medium mb-2">
                      {pdfFile ? pdfFile.name : "📄 Trascina qui il PDF o 📷 clicca per selezionare"}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Drag & drop oppure clicca • PDF, JPG, PNG, WebP supportati
                    </div>
                  </label>
                )}

                {pdfFile && (
                  <div className="mt-4 flex justify-center gap-3">
                    <Button
                      onClick={handlePdfAnalyze}
                      disabled={pdfAnalyzing}
                      data-testid="analyze-pdf-btn"
                    >
                      {pdfAnalyzing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Analisi AI in corso...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Analizza con AI
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => { setPdfFile(null); setPdfFileBase64(null); setPdfData(null); }}
                    >
                      Rimuovi
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* PDF Results */}
          {pdfData && (
            <div className="space-y-4">
              {/* Confidence Summary Bar */}
              {pdfData.field_confidence && Object.keys(pdfData.field_confidence).length > 0 && (
                <Card className="bg-card border-border">
                  <CardContent className="pt-5 pb-4">
                    <ConfidenceBar fieldConfidence={pdfData.field_confidence} />
                  </CardContent>
                </Card>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Client Data Card - Using reusable component */}
                <div className="space-y-3">
                  {/* Client Override Selector */}
                  <Card className="bg-card border-border">
                    <CardContent className="pt-4 pb-3">
                      {clientOverride ? (
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <UserCheck className="h-4 w-4 text-green-600 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate" data-testid="override-client-name">{clientOverride.name}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {[clientOverride.fiscal_code, clientOverride.email].filter(Boolean).join(" · ")}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => { setClientOverride(null); setShowClientSearch(false); setClientSearchQuery(""); }}
                              data-testid="clear-client-override-btn"
                            >
                              <Undo2 className="h-3 w-3 mr-1" />
                              Usa dati AI
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => { setShowClientSearch(true); setClientOverride(null); }}
                             aria-label="Modifica">
                              <Edit className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ) : showClientSearch ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="relative flex-1">
                              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                              <Input
                                value={clientSearchQuery}
                                onChange={(e) => setClientSearchQuery(e.target.value)}
                                placeholder="Cerca cliente per nome, CF, email..."
                                className="pl-8 h-8 text-sm"
                                autoFocus
                                data-testid="import-client-search-input"
                              />
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 flex-shrink-0"
                              onClick={() => { setShowClientSearch(false); setClientSearchQuery(""); }}
                             aria-label="Chiudi">
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          {clientSearchQuery.length >= 2 && (
                            <div className="max-h-48 overflow-y-auto border border-border rounded-md">
                              {allClients
                                .filter(c => {
                                  const q = clientSearchQuery.toLowerCase();
                                  return (c.name?.toLowerCase().includes(q)) ||
                                    (c.fiscal_code?.toLowerCase().includes(q)) ||
                                    (c.email?.toLowerCase().includes(q)) ||
                                    (c.phone?.includes(q));
                                })
                                .slice(0, 8)
                                .map((c) => (
                                  <button
                                    key={c.client_id}
                                    type="button"
                                    onClick={() => {
                                      setClientOverride(c);
                                      setShowClientSearch(false);
                                      setClientSearchQuery("");
                                    }}
                                    className="w-full p-2.5 text-left hover:bg-accent transition-colors border-b border-border last:border-0"
                                    data-testid={`import-client-option-${c.client_id}`}
                                  >
                                    <p className="text-sm font-medium">{c.name}</p>
                                    <p className="text-[11px] text-muted-foreground">
                                      {[c.fiscal_code, c.email, c.phone].filter(Boolean).join(" · ")}
                                    </p>
                                  </button>
                                ))}
                              {allClients.filter(c => {
                                const q = clientSearchQuery.toLowerCase();
                                return (c.name?.toLowerCase().includes(q)) || (c.fiscal_code?.toLowerCase().includes(q)) || (c.email?.toLowerCase().includes(q));
                              }).length === 0 && (
                                <div className="p-3 text-xs text-muted-foreground text-center">
                                  Nessun cliente trovato
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            {pdfData.client_exists 
                              ? `Cliente trovato: ${pdfData.existing_client?.name || 'N/D'}`
                              : "Verrà creato un nuovo cliente dai dati AI"
                            }
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => setShowClientSearch(true)}
                            data-testid="change-client-btn"
                          >
                            <Users className="h-3 w-3 mr-1" />
                            Seleziona cliente
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <ClientDataCard
                    clientData={pdfData.client_data}
                    isEditing={editingPdf}
                    onToggleEdit={() => setEditingPdf(!editingPdf)}
                    onUpdateField={updatePdfClientData}
                    clientExists={clientOverride ? true : pdfData.client_exists}
                    existingClient={clientOverride || pdfData.existing_client}
                    matchReason={clientOverride ? "Selezionato manualmente" : pdfData.match_reason}
                    createAlias={pdfCreateAlias}
                    onCreateAliasChange={setPdfCreateAlias}
                    fieldConfidence={pdfData.field_confidence || {}}
                  />
                </div>

                {/* Policy Data Card - Using reusable component */}
                <PolicyDataCard
                  policyData={pdfData.policy_data}
                  isEditing={editingPdf}
                  onUpdateField={updatePdfPolicyData}
                  companySuggestions={pdfData.company_suggestions}
                  fieldConfidence={pdfData.field_confidence || {}}
                  servicesCatalog={servicesCatalog}
                  onAddService={addServiceToPdf}
                  onRemoveService={removeServiceFromPdf}
                />
              </div>
            </div>
          )}
          {/* Detected Documents Card - Shows when multiple document types found */}
          {pdfData && pdfData.has_multiple_documents && pdfData.detected_documents?.length > 0 && (
            <Card className="border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  📑 Documenti Multipli Rilevati
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  L'AI ha rilevato <strong>{pdfData.detected_documents.length}</strong> documenti diversi in questo PDF.
                  Puoi modificare il tipo di ciascun documento prima di salvare.
                </p>
                <div className="space-y-2">
                  {pdfData.detected_documents.map((doc, idx) => {
                    const licensePlate = pdfData.policy_data?.license_plate?.toUpperCase() || "";
                    const isVehiclePolicy = isVehiclePolicyType(pdfData.policy_data?.policy_type);
                    
                    const typeLabels = {
                      "polizza": { 
                        label: "Polizza", 
                        icon: "📋", 
                        folder: (isVehiclePolicy && licensePlate) ? `Polizze/${licensePlate}` : "Polizze" 
                      },
                      "carta_identita": { label: "Carta Identità", icon: "🪪", folder: "Documenti" },
                      "patente": { label: "Patente", icon: "🚗", folder: "Documenti" },
                      "tessera_sanitaria": { label: "Tessera Sanitaria", icon: "🏥", folder: "Documenti" },
                      "codice_fiscale": { label: "Codice Fiscale", icon: "📇", folder: "Documenti" },
                      "permesso_soggiorno": { label: "Permesso Soggiorno", icon: "📜", folder: "Documenti" },
                      "libretto_circolazione": { 
                        label: "Libretto", 
                        icon: "📖", 
                        folder: licensePlate ? `Polizze/${licensePlate}` : "Documenti" 
                      },
                      "visura_camerale": { label: "Visura Camerale", icon: "🏢", folder: "Documenti" },
                      "altro": { label: "Documento", icon: "📄", folder: "Documenti" }
                    };
                    const info = typeLabels[doc.type] || typeLabels["altro"];
                    const isPlateFolder = info.folder.includes("/");
                    
                    return (
                      <div key={idx} className="flex items-center justify-between bg-white dark:bg-background p-2 rounded-lg gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span>{info.icon}</span>
                          <Select 
                            value={doc.type} 
                            onValueChange={(newType) => {
                              const updatedDocs = [...pdfData.detected_documents];
                              updatedDocs[idx] = { ...updatedDocs[idx], type: newType };
                              setPdfData({ ...pdfData, detected_documents: updatedDocs });
                            }}
                          >
                            <SelectTrigger className="h-8 text-sm w-[180px]" data-testid={`doc-type-select-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="polizza">Polizza</SelectItem>
                              <SelectItem value="carta_identita">Carta Identit&agrave;</SelectItem>
                              <SelectItem value="patente">Patente</SelectItem>
                              <SelectItem value="tessera_sanitaria">Tessera Sanitaria</SelectItem>
                              <SelectItem value="codice_fiscale">Codice Fiscale</SelectItem>
                              <SelectItem value="permesso_soggiorno">Permesso Soggiorno</SelectItem>
                              <SelectItem value="libretto_circolazione">Libretto</SelectItem>
                              <SelectItem value="visura_camerale">Visura Camerale</SelectItem>
                              <SelectItem value="attestato_rischio">Attestato di Rischio</SelectItem>
                              <SelectItem value="quietanza">Quietanza</SelectItem>
                              <SelectItem value="altro">Altro</SelectItem>
                            </SelectContent>
                          </Select>
                          <span className="text-xs text-muted-foreground">(Pag. {doc.page + 1})</span>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded whitespace-nowrap ${isPlateFolder ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' : 'bg-muted'}`}>
                          &rarr; {info.folder}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-3">
                  💡 Al salvataggio, ogni documento verrà estratto e archiviato separatamente
                  {pdfData.policy_data?.license_plate && (
                    <span className="block mt-1">
                      🚗 I documenti veicolo verranno salvati nella cartella <strong>{pdfData.policy_data.license_plate.toUpperCase()}</strong>
                    </span>
                  )}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Validation Warnings */}
          {pdfData && pdfData.warnings?.length > 0 && (
            <Card className="border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950" data-testid="ai-validation-warnings">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-amber-800 dark:text-amber-200">
                  <AlertTriangle className="h-5 w-5" />
                  Verifica Dati Estratti
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-amber-700 dark:text-amber-300 mb-2">
                  L'AI ha rilevato possibili imprecisioni. Controlla i campi evidenziati prima di salvare.
                </p>
                <div className="space-y-1">
                  {pdfData.warnings.map((w, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-200 bg-amber-100 dark:bg-amber-900 px-3 py-1.5 rounded">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span><strong>{w.field}:</strong> {w.message}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Commission Calculator - Using reusable component */}
          {pdfData && pdfData.policy_data?.premium && (
            <CommissionCalculator
              premium={pdfData.policy_data.premium}
              totalCollected={pdfData.total_collected}
              consultationFee={pdfData.policy_data?.consultation_fee}
              additionalServices={pdfData.policy_data?.additional_services}
              onTotalCollectedChange={(totalValue, commission) => {
                setPdfData(prev => ({
                  ...prev,
                  total_collected: totalValue,
                  policy_data: {
                    ...prev.policy_data,
                    consultation_fee: commission
                  }
                }));
              }}
            />
          )}

          {/* Create Button */}
          {pdfData && (
            <div className="flex justify-center gap-3">
              <Button
                variant="outline"
                onClick={() => { setPdfData(null); setPdfFile(null); setPdfFileBase64(null); setClientOverride(null); setShowClientSearch(false); setClientSearchQuery(""); }}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Annulla
              </Button>
              <Button
                onClick={handlePdfCreate}
                disabled={pdfCreating}
                className="bg-green-600 hover:bg-green-700"
                data-testid="create-from-pdf-btn"
              >
                {pdfCreating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creazione...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {pdfData.client_exists || clientOverride ? "Crea Polizza" : "Crea Cliente + Polizza"}
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Instructions */}
          {!pdfData && !pdfFile && (
            <Card className="bg-muted/50 border-border">
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-3">Come funziona:</h3>
                <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                  <li>Carica un <strong>PDF</strong> o scatta una <strong>foto</strong> della polizza 📷</li>
                  <li>L'AI analizzerà il documento ed estrarrà i dati</li>
                  <li>Verifica i dati estratti e modificali se necessario</li>
                  <li>Clicca "Crea" per salvare cliente e polizza</li>
                </ol>
                <p className="mt-4 text-sm text-primary">
                  💡 Dal telefono puoi scattare direttamente una foto!
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Formati supportati: PDF, JPG, PNG, WEBP, HEIC
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* EXCEL TAB */}
        <TabsContent value="excel" className="space-y-4">
      {/* Import Settings */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Configurazione Import
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Type Selection */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Tipo di import:</span>
              <Select value={importType} onValueChange={(v) => { setImportType(v); setPreview(null); }}>
                <SelectTrigger className="w-[180px]" data-testid="import-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="clients">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Clienti
                    </div>
                  </SelectItem>
                  <SelectItem value="policies">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Polizze
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              data-testid="download-template-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Scarica Template
            </Button>
          </div>

          {/* File Upload with Drag & Drop */}
          <div 
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
              isDraggingExcel 
                ? 'border-primary bg-primary/10 scale-[1.02]' 
                : 'border-border hover:border-primary/50 hover:bg-muted/50'
            }`}
            onDragOver={handleExcelDragOver}
            onDragEnter={handleExcelDragEnter}
            onDragLeave={handleExcelDragLeave}
            onDrop={handleExcelDrop}
            data-testid="excel-dropzone"
          >
            <FileSpreadsheet className={`h-12 w-12 mx-auto mb-4 transition-colors ${isDraggingExcel ? 'text-primary' : 'text-muted-foreground'}`} />
            
            <input
              id="excel-file-input"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
            />
            
            {isDraggingExcel ? (
              <div className="text-lg font-medium text-primary">
                Rilascia il file Excel qui!
              </div>
            ) : (
              <label
                htmlFor="excel-file-input"
                className="cursor-pointer"
              >
                <div className="text-lg font-medium mb-2">
                  {file ? file.name : "📊 Trascina qui il file Excel o clicca per selezionare"}
                </div>
                <div className="text-sm text-muted-foreground">
                  Drag & drop oppure clicca • Formati: .xlsx, .xls
                </div>
              </label>
            )}

            {file && (
              <div className="mt-4 flex justify-center gap-3">
                <Button
                  onClick={handleAnalyze}
                  disabled={analyzing}
                  data-testid="analyze-btn"
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Analisi AI in corso...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Analizza con AI
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setFile(null); setPreview(null); }}
                >
                  Rimuovi
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Preview Results */}
      {preview && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-card border-border">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Righe Totali</p>
                    <p className="text-2xl font-bold">{preview.total_rows}</p>
                  </div>
                  <FileSpreadsheet className="h-8 w-8 text-blue-500 opacity-80" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Righe Valide</p>
                    <p className="text-2xl font-bold text-green-600">{preview.valid_rows}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500 opacity-80" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Avvisi</p>
                    <p className="text-2xl font-bold text-amber-600">{preview.warnings?.length || 0}</p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-amber-500 opacity-80" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Errori</p>
                    <p className="text-2xl font-bold text-red-600">{preview.errors?.length || 0}</p>
                  </div>
                  <XCircle className="h-8 w-8 text-red-500 opacity-80" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Column Mapping */}
          {preview.column_mapping && Object.keys(preview.column_mapping).length > 0 && (
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Mappatura Colonne (AI)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(preview.column_mapping).map(([excel, field]) => (
                    <div key={excel} className="bg-muted px-3 py-1 rounded-full text-sm">
                      <span className="text-muted-foreground">{excel}</span>
                      <span className="mx-2">→</span>
                      <span className="font-medium">{field}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Warnings */}
          {preview.warnings?.length > 0 && (
            <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2 text-amber-800 dark:text-amber-200">
                  <AlertTriangle className="h-5 w-5" />
                  Avvisi ({preview.warnings.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1 text-sm text-amber-700 dark:text-amber-300">
                  {preview.warnings.slice(0, 10).map((w, i) => (
                    <li key={i}>• {w}</li>
                  ))}
                  {preview.warnings.length > 10 && (
                    <li className="text-muted-foreground">... e altri {preview.warnings.length - 10} avvisi</li>
                  )}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Errors */}
          {preview.errors?.length > 0 && (
            <Card className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2 text-red-800 dark:text-red-200">
                  <XCircle className="h-5 w-5" />
                  Errori ({preview.errors.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1 text-sm text-red-700 dark:text-red-300">
                  {preview.errors.slice(0, 10).map((e, i) => (
                    <li key={i}>• {e}</li>
                  ))}
                  {preview.errors.length > 10 && (
                    <li className="text-muted-foreground">... e altri {preview.errors.length - 10} errori</li>
                  )}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Data Preview Table */}
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">
                Anteprima Dati ({preview.preview_data?.length || 0} di {preview.valid_rows})
              </CardTitle>
              <Button
                onClick={handleImport}
                disabled={importing || preview.valid_rows === 0}
                className="bg-green-600 hover:bg-green-700"
                data-testid="import-btn"
              >
                {importing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Importazione...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Importa {preview.valid_rows} {importType === "clients" ? "Clienti" : "Polizze"}
                  </>
                )}
              </Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      {getFieldsForType().map((field) => (
                        <TableHead key={field} className="capitalize">
                          {field.replace("_", " ")}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.preview_data?.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-muted-foreground">{row._row_num}</TableCell>
                        {getFieldsForType().map((field) => (
                          <TableCell key={field}>
                            {field === "premium" || field === "consultation_fee" 
                              ? (row[field] ? `€${row[field]}` : "-")
                              : (row[field] || "-")}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Instructions */}
      {!preview && (
        <Card className="bg-muted/50 border-border">
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-3">Come funziona:</h3>
            <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
              <li>Seleziona il tipo di import (Clienti o Polizze)</li>
              <li>Scarica il template Excel per vedere il formato corretto</li>
              <li>Compila il file con i tuoi dati</li>
              <li>Carica il file e clicca "Analizza con AI"</li>
              <li>L'AI mapperà automaticamente le colonne ai campi corretti</li>
              <li>Verifica l'anteprima e clicca "Importa" per confermare</li>
            </ol>
            <p className="mt-4 text-sm text-primary">
              💡 L'AI riconosce automaticamente le colonne anche se hanno nomi diversi dal template!
            </p>
          </CardContent>
        </Card>
      )}
        </TabsContent>

        {/* EMAIL TAB */}
        <TabsContent value="email" className="space-y-4">
          {/* Email Settings Card */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                Import da Email Gmail
              </CardTitle>
              <CardDescription>
                Configura un account Gmail dedicato per ricevere e importare automaticamente le polizze
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {emailSettingsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  {/* Enable/Disable Toggle */}
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <Label className="font-medium">Abilita Import Email</Label>
                      <p className="text-xs text-muted-foreground">Il sistema controllerà le nuove email ogni 5 minuti</p>
                    </div>
                    <Switch
                      checked={emailSettings.enabled}
                      onCheckedChange={(checked) => setEmailSettings(prev => ({ ...prev, enabled: checked }))}
                    />
                  </div>

                  {/* Gmail Credentials */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Email Gmail</Label>
                      <Input
                        type="email"
                        placeholder="polizze@gmail.com"
                        value={emailSettings.email_address}
                        onChange={(e) => setEmailSettings(prev => ({ ...prev, email_address: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>
                        App Password
                        {emailSettings.has_password && (
                          <span className="ml-2 text-xs text-green-600">✓ Configurata</span>
                        )}
                      </Label>
                      <Input
                        type="password"
                        placeholder={emailSettings.has_password ? "••••••••••••••••" : "xxxx xxxx xxxx xxxx"}
                        value={emailSettings.app_password}
                        onChange={(e) => setEmailSettings(prev => ({ ...prev, app_password: e.target.value }))}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        <a 
                          href="https://myaccount.google.com/apppasswords" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          Genera App Password →
                        </a>
                      </p>
                    </div>
                  </div>

                  {/* Additional Settings */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Elimina email dopo (giorni)</Label>
                      <Select
                        value={emailSettings.auto_delete_after_days.toString()}
                        onValueChange={(value) => setEmailSettings(prev => ({ ...prev, auto_delete_after_days: parseInt(value) }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="30">30 giorni</SelectItem>
                          <SelectItem value="90">90 giorni (3 mesi)</SelectItem>
                          <SelectItem value="180">180 giorni (6 mesi)</SelectItem>
                          <SelectItem value="365">365 giorni (1 anno)</SelectItem>
                          <SelectItem value="0">Mai (disabilitato)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <Label className="text-sm">Elaborazione automatica</Label>
                        <p className="text-xs text-muted-foreground">L'AI analizza subito gli allegati</p>
                      </div>
                      <Switch
                        checked={emailSettings.auto_process}
                        onCheckedChange={(checked) => setEmailSettings(prev => ({ ...prev, auto_process: checked }))}
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3">
                    <Button onClick={saveEmailSettings} disabled={emailSaving}>
                      {emailSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      Salva Impostazioni
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={testEmailConnection} 
                      disabled={emailTesting || !emailSettings.email_address}
                    >
                      {emailTesting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                      Test Connessione
                    </Button>
                    <Button 
                      variant="secondary" 
                      onClick={fetchEmailsNow} 
                      disabled={emailFetching || !emailSettings.has_password}
                    >
                      {emailFetching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                      Controlla Ora
                    </Button>
                  </div>

                  {/* Help Section */}
                  <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg text-sm">
                    <h4 className="font-semibold mb-2">📧 Come configurare:</h4>
                    <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                      <li>Crea un account Gmail dedicato (es: polizze.tuaagenzia@gmail.com)</li>
                      <li>Abilita la verifica in 2 passaggi nelle impostazioni di sicurezza Google</li>
                      <li>Genera una "Password per le app" (link sopra)</li>
                      <li>Inserisci email e password qui e salva</li>
                      <li>Inoltra o invia le email con PDF delle polizze a questo account</li>
                    </ol>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Pending Imports Card */}
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5 text-orange-500" />
                  Import in Attesa
                </CardTitle>
                <CardDescription>Email ricevute da elaborare</CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={loadPendingImports} disabled={pendingLoading}>
                <RefreshCw className={`h-4 w-4 ${pendingLoading ? 'animate-spin' : ''}`} />
              </Button>
            </CardHeader>
            <CardContent>
              {pendingLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : pendingImports.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Nessun import in attesa</p>
                  <p className="text-xs mt-1">Le nuove email appariranno qui</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingImports.map((imp) => (
                    <div 
                      key={imp.import_id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer"
                      onClick={() => analyzeEmailImport(imp)}
                    >
                      <div className="flex items-center gap-3">
                        {imp.attachment_type === 'pdf' ? (
                          <FileText className="h-8 w-8 text-red-500" />
                        ) : (
                          <FileUp className="h-8 w-8 text-blue-500" />
                        )}
                        <div>
                          <p className="font-medium text-sm">{imp.attachment_name}</p>
                          <p className="text-xs text-muted-foreground">
                            Da: {imp.email_from?.split('<')[0]?.trim() || imp.email_from}
                          </p>
                          <p className="text-xs text-muted-foreground">{imp.email_subject}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {imp.status === 'processing' ? (
                          <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">In elaborazione</span>
                        ) : imp.ai_analysis ? (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Analizzato</span>
                        ) : (
                          <span className="text-xs bg-muted px-2 py-1 rounded">In attesa</span>
                        )}
                        <Sparkles className="h-4 w-4 text-primary" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Loading State */}
          {importAnalyzing && (
            <Card className="bg-card border-border">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">Analisi AI in corso...</p>
                <p className="text-xs text-muted-foreground mt-2">Sto estraendo i dati dal documento...</p>
              </CardContent>
            </Card>
          )}

          {/* Email Import Editor - Using reusable components */}
          {emailImportData && !importAnalyzing && (
            <>
              {/* Source Info Banner */}
              <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                <Mail className="h-5 w-5 text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium">{selectedImport?.attachment_name}</p>
                  <p className="text-xs text-muted-foreground">
                    Da: {selectedImport?.email_from?.split('<')[0]?.trim()} | {selectedImport?.email_subject}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setEmailImportData(null); setSelectedImport(null); }}>
                  <XCircle className="h-4 w-4" />
                </Button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Client Data Card - Using reusable component */}
                <ClientDataCard
                  clientData={emailImportData.client_data}
                  isEditing={editingEmailImport}
                  onToggleEdit={() => setEditingEmailImport(!editingEmailImport)}
                  onUpdateField={updateEmailClientData}
                  clientExists={emailImportData.client_exists}
                  existingClient={emailImportData.existing_client}
                  matchReason={emailImportData.match_reason}
                  createAlias={emailCreateAlias}
                  onCreateAliasChange={setEmailCreateAlias}
                  fieldConfidence={emailImportData.field_confidence || {}}
                />

                {/* Policy Data Card - Using reusable component */}
                <PolicyDataCard
                  policyData={emailImportData.policy_data}
                  isEditing={editingEmailImport}
                  onUpdateField={updateEmailPolicyData}
                  companySuggestions={emailImportData.company_suggestions}
                  fieldConfidence={emailImportData.field_confidence || {}}
                  servicesCatalog={servicesCatalog}
                  onAddService={addServiceToEmailImport}
                  onRemoveService={removeServiceFromEmailImport}
                />
              </div>

              {/* Commission Calculator - Using reusable component */}
              {emailImportData.policy_data?.premium && (
                <CommissionCalculator
                  premium={emailImportData.policy_data.premium}
                  totalCollected={emailImportData.total_collected}
                  consultationFee={emailImportData.policy_data?.consultation_fee}
                  additionalServices={emailImportData.policy_data?.additional_services}
                  onTotalCollectedChange={(totalValue, commission) => {
                    setEmailImportData(prev => ({
                      ...prev,
                      total_collected: totalValue,
                      policy_data: {
                        ...prev.policy_data,
                        consultation_fee: commission
                      }
                    }));
                  }}
                />
              )}

              {/* Document Upload Info */}
              {emailImportData.policy_data?.license_plate && (
                <div className="p-3 bg-amber-50 dark:bg-amber-950 rounded-lg text-sm">
                  <p className="text-amber-800 dark:text-amber-200">
                    🚗 Il documento verrà salvato nella cartella <strong>{emailImportData.policy_data.license_plate.toUpperCase()}</strong>
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => discardEmailImport(selectedImport?.import_id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Scarta
                </Button>
                <Button
                  onClick={handleEmailImportCreate}
                  disabled={importConfirming}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {importConfirming ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  {emailImportData.client_exists ? 'Crea Polizza' : 'Crea Cliente + Polizza'}
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        {/* HISTORY TAB */}
        <TabsContent value="history" className="space-y-4">
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <History className="h-5 w-5 text-primary" />
                  Storico Import
                </CardTitle>
                <CardDescription>
                  Cronologia di tutti gli import effettuati
                </CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={loadImportHistory} disabled={historyLoading}>
                <RefreshCw className={`h-4 w-4 ${historyLoading ? 'animate-spin' : ''}`} />
              </Button>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : importHistory.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Nessun import nello storico</p>
                  <Button variant="link" onClick={loadImportHistory}>Aggiorna</Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Origine</TableHead>
                      <TableHead>Azione</TableHead>
                      <TableHead>Entità</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importHistory.map((entry) => (
                      <TableRow key={entry.history_id} className={entry.undone ? 'opacity-50' : ''}>
                        <TableCell className="text-sm">
                          {formatDateTime(entry.created_at)}
                        </TableCell>
                        <TableCell>
                          <span className={`text-xs px-2 py-1 rounded ${
                            entry.import_type === 'email' ? 'bg-blue-100 text-blue-800' :
                            entry.import_type === 'pdf' ? 'bg-red-100 text-red-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {entry.import_type === 'email' ? '📧 Email' :
                             entry.import_type === 'pdf' ? '📄 PDF' : '📊 Excel'}
                          </span>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate" title={entry.source_name}>
                          {entry.source_name}
                        </TableCell>
                        <TableCell>
                          {entry.action === 'created_client' ? '👤 Creato cliente' :
                           entry.action === 'created_policy' ? '📋 Creata polizza' :
                           entry.action === 'updated_client' ? '✏️ Aggiornato cliente' :
                           entry.action}
                        </TableCell>
                        <TableCell className="font-medium">{entry.entity_name || '-'}</TableCell>
                        <TableCell className="text-right">
                          {entry.undone ? (
                            <span className="text-xs text-muted-foreground">Annullato</span>
                          ) : entry.can_undo ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (confirm('Vuoi annullare questa operazione? L\'entità creata verrà eliminata.')) {
                                  undoImport(entry.history_id);
                                }
                              }}
                            >
                              <Undo2 className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
